import dayjs from "dayjs";
import {
    DEFAULT_EVENT_PUBLIC_ASSET_MODE,
    EventPublicAssetModeValue,
    normalizeEventPublicAssetMode
} from "../../eventPublicAssetMode";
import {atiColors} from "../../Theme/atiTheme";

const DEFAULT_PRIMARY_COLOR = atiColors.blue;
const DEFAULT_SECONDARY_COLOR = atiColors.orange;

export type EventTemplateBasicSetup = {
    id: number;
    code?: string | null;
    name?: string | null;
    nameHr?: string | null;
    nameEn?: string | null;
    place?: string | null;
    address?: string | null;
    description?: string | null;
    eventInformation?: string | null;
    eventInformationHr?: string | null;
    eventInformationEn?: string | null;
    onSiteRegistrationInstructions?: string | null;
    onSiteRegistrationInstructionsHr?: string | null;
    onSiteRegistrationInstructionsEn?: string | null;
    hasParticipationFee?: boolean | null;
    startDate?: string | null;
    endDate?: string | null;
};

export type EventTemplateBranding = {
    id?: number | null;
    eventId: number;
    logoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    publicAssetMode?: string | number | null;
    programPdfUrl?: string | null;
    venueLayoutImageUrl?: string | null;
    sponsorsImageUrl?: string | null;
    badgeTemplateUrl?: string | null;
    badgeBackTemplateUrl?: string | null;
    badgeFirstNameX?: number | null;
    badgeFirstNameY?: number | null;
    badgeLastNameX?: number | null;
    badgeLastNameY?: number | null;
    badgeCompanyX?: number | null;
    badgeCompanyY?: number | null;
    badgeQrCodeX?: number | null;
    badgeQrCodeY?: number | null;
    badgeTextFieldsJson?: string | null;
    headerImageUrl?: string | null;
    certificateTemplateUrl?: string | null;
    certificateParticipantNameX?: number | null;
    certificateParticipantNameY?: number | null;
    certificateTextFieldsJson?: string | null;
};

export type EventTemplateSettingsQueryData = {
    eventById?: EventTemplateBasicSetup | null;
    eventBrandings?: {
        items?: Array<EventTemplateBranding | null> | null;
        totalCount: number;
    } | null;
};

export type EventTemplateBrandingOverrides = Partial<EventTemplateBranding>;

const textValue = (value?: string | null) => value?.trim() ?? "";

const dateValue = (value?: string | null) => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.startOf("day").toISOString() : dayjs().startOf("day").toISOString();
};

export const getEventTemplateBranding = (
    data?: EventTemplateSettingsQueryData
): EventTemplateBranding | null =>
    (data?.eventBrandings?.items ?? []).find((item): item is EventTemplateBranding => !!item) ?? null;

export const buildEventTemplateSettingsSaveInput = (
    event: EventTemplateBasicSetup,
    branding: EventTemplateBranding | null | undefined,
    overrides: EventTemplateBrandingOverrides
) => {
    const merged = {
        ...(branding ?? {}),
        ...overrides
    };
    const eventName = textValue(event.name);

    return {
        id: event.id,
        code: textValue(event.code) || `EVENT-${event.id}`,
        name: eventName,
        nameHr: textValue(event.nameHr),
        nameEn: textValue(event.nameEn),
        place: textValue(event.place),
        address: textValue(event.address),
        eventInformationHr: textValue(event.eventInformationHr),
        eventInformationEn: textValue(event.eventInformationEn),
        onSiteRegistrationInstructionsHr: textValue(event.onSiteRegistrationInstructionsHr),
        onSiteRegistrationInstructionsEn: textValue(event.onSiteRegistrationInstructionsEn),
        hasParticipationFee: event.hasParticipationFee ?? true,
        startDate: dateValue(event.startDate),
        endDate: dateValue(event.endDate),
        logoUrl: textValue(merged.logoUrl),
        primaryColor: textValue(merged.primaryColor) || DEFAULT_PRIMARY_COLOR,
        secondaryColor: textValue(merged.secondaryColor) || DEFAULT_SECONDARY_COLOR,
        publicAssetMode: normalizeEventPublicAssetMode(merged.publicAssetMode ?? DEFAULT_EVENT_PUBLIC_ASSET_MODE) as EventPublicAssetModeValue,
        programPdfUrl: textValue(merged.programPdfUrl),
        venueLayoutImageUrl: textValue(merged.venueLayoutImageUrl),
        sponsorsImageUrl: textValue(merged.sponsorsImageUrl),
        badgeTemplateUrl: textValue(merged.badgeTemplateUrl),
        badgeBackTemplateUrl: textValue(merged.badgeBackTemplateUrl),
        badgeFirstNameX: merged.badgeFirstNameX ?? null,
        badgeFirstNameY: merged.badgeFirstNameY ?? null,
        badgeLastNameX: merged.badgeLastNameX ?? null,
        badgeLastNameY: merged.badgeLastNameY ?? null,
        badgeCompanyX: merged.badgeCompanyX ?? null,
        badgeCompanyY: merged.badgeCompanyY ?? null,
        badgeQrCodeX: merged.badgeQrCodeX ?? null,
        badgeQrCodeY: merged.badgeQrCodeY ?? null,
        badgeTextFieldsJson: textValue(merged.badgeTextFieldsJson),
        headerImageUrl: textValue(merged.headerImageUrl),
        certificateTemplateUrl: textValue(merged.certificateTemplateUrl),
        certificateParticipantNameX: merged.certificateParticipantNameX ?? null,
        certificateParticipantNameY: merged.certificateParticipantNameY ?? null,
        certificateTextFieldsJson: textValue(merged.certificateTextFieldsJson)
    };
};
